// invest_FE/src/components/topbar/GlobalAnnouncement.js
import React, { useState, useEffect, useContext } from 'react';
import { getActiveAnnouncement } from '../../services/announcementService';
import { ConfigContext } from '../../context/ConfigContext';
import { UserContext } from '../../context/UserContext';
import { useBanner } from '../../context/BannerContext';

const DISMISSED_ANNOUNCEMENT_ID_KEY = 'dismissedAnnouncementId';
const POLLING_INTERVAL_MS = 30 * 1000; // Check for new announcements every 30 seconds

// --- Custom Hook for Countdown Logic ---
const useCountdown = (targetDate) => {
  const countDownDate = new Date(targetDate).getTime();

  const [countDown, setCountDown] = useState(countDownDate - new Date().getTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown(countDownDate - new Date().getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [countDownDate]);

  if (countDown < 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isFinished: true };
  }

  const days = Math.floor(countDown / (1000 * 60 * 60 * 24));
  const hours = Math.floor((countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, isFinished: false };
};

// --- Helper component to manage countdown rendering ---
const AnnouncementMessage = ({ announcement, color }) => {
  const { message, countdownTarget, type } = announcement;
  const countdown = useCountdown(countdownTarget);
  const { setLocalShutdownStatus, startFastPolling } = useContext(ConfigContext);
  const { user } = useContext(UserContext);

  useEffect(() => {
    // Only set a timer for 'shutdown' type announcements with a valid target date.
    if (type !== 'shutdown' || !countdownTarget) {
      return;
    }

    const targetTime = new Date(countdownTarget).getTime();
    const now = new Date().getTime();
    const delay = targetTime - now;

    if (delay <= 0) {
      return; // Don't set a timer for the past.
    }

    const timerId = setTimeout(() => {
      if (!user.isAdmin) {
        setLocalShutdownStatus('SHUTDOWN_INITIATED');
      } else {
        startFastPolling();
      }
    }, delay);

    return () => clearTimeout(timerId); // Cleanup the timer
  }, [countdownTarget, type, user.isAdmin, setLocalShutdownStatus, startFastPolling]);

  // If no target date is set, or if the message contains no placeholders, just return the plain message.
  if (!countdownTarget || (!message.includes('[countdown]') && !message.includes('[datetime]'))) {
    // The dangerouslySetInnerHTML is now handled by the Banner component.
    return message;
  }

  // Use a regex to split the message by placeholders, keeping the placeholder names.
  const placeholderRegex = /\[(countdown|datetime)\]/g;
  const parts = message.split(placeholderRegex);

  const renderedParts = parts.map((part, index) => {
    if (part === 'countdown') {
      const format = (num) => num.toString().padStart(2, '0');
      let timerString = '00:00';
      if (!countdown.isFinished) {
        timerString = `${format(countdown.minutes)}:${format(countdown.seconds)}`;
        if (countdown.hours > 0 || countdown.days > 0) {
          const totalHours = countdown.days * 24 + countdown.hours;
          timerString = `${format(totalHours)}:${timerString}`;
        }
      }
      return (
        <span key={`countdown-${index}`} className="countdown-timer" style={{ color }}>
          {timerString}
        </span>
      );
    }

    if (part === 'datetime') {
      const date = new Date(countdownTarget);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      const dateTimeString = `${month}-${day}-${year}`;
      return (
        <span key={`datetime-${index}`} className="datetime-display" style={{ color }}>
          {dateTimeString}
        </span>
      );
    }

    // It's a regular text part, which could be an empty string.
    // Wrap it in a span to ensure consistent flex item behavior.
    return part ? <span key={`text-${index}`}>{part}</span> : null;
  });

  return <>{renderedParts}</>;
};

// --- Main Component ---
const GlobalAnnouncement = () => {
  const { addBanner, removeBanner } = useBanner();
  const [currentAnnouncementId, setCurrentAnnouncementId] = useState(null);

  useEffect(() => {
    // --- Prerender Gating (Step 2 from plan) ---
    if (navigator.userAgent.includes('ReactSnap')) {
      return; // Do not fetch or set up intervals during prerender
    }

    const fetchAndProcessAnnouncement = async () => {
      try {
        const data = await getActiveAnnouncement();
        const dismissedId = sessionStorage.getItem(DISMISSED_ANNOUNCEMENT_ID_KEY);

        if (data?._id) {
          // If there's an active announcement from the server...
          if (data._id !== dismissedId && data._id !== currentAnnouncementId) {
            // ...and it's not one we've dismissed or are currently showing...

            // If there's an old announcement banner showing, remove it first.
            if (currentAnnouncementId) {
              removeBanner(currentAnnouncementId);
            }

            // Define the custom dismiss handler
            const handleDismiss = () => {
              sessionStorage.setItem(DISMISSED_ANNOUNCEMENT_ID_KEY, data._id);
            };

            // Map background color to banner type
            const colorMap = {
              '#FFC107': 'warning', // Yellow
              '#28A745': 'success', // Green
              '#FD7E14': 'warning', // Orange
              '#DC3545': 'error', // Red
            };
            const bannerType = colorMap[data.backgroundColor] || 'info';
            const fontColor = data.backgroundColor === '#FFC107' ? '#212529' : '#FFFFFF';

            // Add the new banner
            addBanner(<AnnouncementMessage announcement={data} color={fontColor} />, bannerType, [], data._id, handleDismiss);
            setCurrentAnnouncementId(data._id);
          }
        } else {
          // No active announcement from server, remove the one we might be showing
          if (currentAnnouncementId) {
            removeBanner(currentAnnouncementId);
            setCurrentAnnouncementId(null);
          }
        }
      } catch (error) {
        // console.error('Could not fetch global announcement:', error);
        // In case of error, also remove any showing announcement
        if (currentAnnouncementId) {
          removeBanner(currentAnnouncementId);
          setCurrentAnnouncementId(null);
        }
      }
    };

    fetchAndProcessAnnouncement();
    const intervalId = setInterval(fetchAndProcessAnnouncement, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [addBanner, removeBanner, currentAnnouncementId]);

  // This component no longer renders anything itself. It just manages adding/removing banners.
  return null;
};

export default GlobalAnnouncement;
